import { Badge, Button, Card } from '@nexora/design-system';
import {
  useEmissionRequests,
  isEmissionSettled,
  type EmissionRequest,
} from './useFiscalInvoices';
import { formatAccessKey } from './accessKey';

/**
 * Acompanhamento da emissão, como a referência
 * `erpone-valida-o-e-emiss-o-fiscal` pede: a escada de situação da operação e
 * um console com o que aconteceu.
 *
 * A fonte é `GET /fiscal/emission-requests?fiscalDocumentId=…` — o registro que
 * o backend mantém de cada tentativa junto à SEFAZ (situação, tentativas,
 * protocolo de autorização, código e mensagem de erro, próxima tentativa,
 * conclusão). A rota existia e nenhuma tela a chamava.
 */

interface Props {
  fiscalDocumentId: string | null;
  accessKey: string | null;
  numero: string | null;
  onVerLista: () => void;
}

/** As etapas que têm fonte. As outras estão listadas em `SEM_FONTE`. */
type EtapaCodigo = 'VALIDACAO' | 'TRANSMISSAO' | 'AUTORIZACAO';

const ETAPA_LABEL: Record<EtapaCodigo, string> = {
  VALIDACAO: 'Validação dos dados',
  TRANSMISSAO: 'Transmissão à SEFAZ',
  AUTORIZACAO: 'Autorização',
};

/**
 * Etapas da referência que este painel **não** mostra, porque não há fonte:
 * nada no backend registra conciliação financeira nem geração de livros a
 * partir da emissão.
 */
const SEM_FONTE = ['Conciliação financeira', 'Geração de livros fiscais'];

type Situacao = 'concluida' | 'andamento' | 'erro' | 'pendente';

const SITUACAO_VARIANT: Record<Situacao, 'success' | 'info' | 'error' | 'neutral'> = {
  concluida: 'success',
  andamento: 'info',
  erro: 'error',
  pendente: 'neutral',
};

const SITUACAO_LABEL: Record<Situacao, string> = {
  concluida: 'concluída',
  andamento: 'em andamento',
  erro: 'erro',
  pendente: 'pendente',
};

function situacaoDaEtapa(
  etapa: EtapaCodigo,
  request: EmissionRequest | undefined,
  accessKey: string | null,
): Situacao {
  /* A emissão só chega aqui depois de o `POST /invoices/emit` responder sem
     erro, então a validação está concluída por construção. */
  if (etapa === 'VALIDACAO') return 'concluida';

  if (!request) {
    /* Sem registro de emissão: a nota foi criada, mas nada foi enfileirado —
       ou a fila ainda não gravou. Não é erro nem sucesso. */
    return etapa === 'TRANSMISSAO' ? 'andamento' : 'pendente';
  }

  const erro = Boolean(request.errorCode || request.errorMessage);
  const assentou = isEmissionSettled(request);

  if (etapa === 'TRANSMISSAO') {
    if (erro) return 'erro';
    return assentou || request.authorizationProtocol ? 'concluida' : 'andamento';
  }

  // AUTORIZACAO
  if (request.authorizationProtocol || accessKey) return 'concluida';
  if (erro) return 'erro';
  return assentou ? 'pendente' : 'andamento';
}

/** Linhas do console, do registro real — sem inventar horário nem passo. */
function linhasDoConsole(request: EmissionRequest | undefined): { marca: string; texto: string }[] {
  if (!request) return [];
  const linhas: { marca: string; texto: string }[] = [
    { marca: 'OK', texto: `Requisição ${request.id} enfileirada (provedor ${request.providerType}).` },
  ];
  if (request.attempts > 0) {
    linhas.push({
      marca: 'INFO',
      texto: `${request.attempts} tentativa${request.attempts === 1 ? '' : 's'} de transmissão.`,
    });
  }
  if (request.sefazState) {
    linhas.push({ marca: 'INFO', texto: `Retorno da SEFAZ: ${request.sefazState}.` });
  }
  if (request.authorizationProtocol) {
    linhas.push({ marca: 'OK', texto: `Protocolo de autorização ${request.authorizationProtocol}.` });
  }
  if (request.errorCode || request.errorMessage) {
    linhas.push({
      marca: 'ERRO',
      texto: [request.errorCode, request.errorMessage].filter(Boolean).join(' — '),
    });
  }
  if (request.nextRetryAt && !isEmissionSettled(request)) {
    linhas.push({
      marca: 'WAIT',
      texto: `Próxima tentativa em ${new Date(request.nextRetryAt).toLocaleString('pt-BR')}.`,
    });
  }
  if (request.processedAt) {
    linhas.push({
      marca: 'OK',
      texto: `Processada em ${new Date(request.processedAt).toLocaleString('pt-BR')}.`,
    });
  }
  return linhas;
}

export function EmissionProgressPanel({
  fiscalDocumentId,
  accessKey,
  numero,
  onVerLista,
}: Props): JSX.Element {
  const requestsQ = useEmissionRequests(fiscalDocumentId);
  const request = requestsQ.data?.data[0];
  const etapas: EtapaCodigo[] = ['VALIDACAO', 'TRANSMISSAO', 'AUTORIZACAO'];
  const console = linhasDoConsole(request);

  return (
    <Card>
      <div className="ep-card">
        <div className="ep-head">
          <div>
            <h3 className="ep-section">
              {accessKey ? 'Documento transmitido' : 'Emissão em processamento'}
            </h3>
            <p className="ep-hint">
              {numero ? `Nota nº ${numero} · ` : ''}
              {accessKey ? formatAccessKey(accessKey) : 'aguardando retorno da SEFAZ'}
            </p>
          </div>
          <Button type="button" variant="outline" onClick={onVerLista}>
            Ver lista
          </Button>
        </div>

        {/* ── Situação da operação ── */}
        <div className="ep-etapas">
          {etapas.map((etapa) => {
            const situacao = situacaoDaEtapa(etapa, request, accessKey);
            return (
              <div key={etapa} className={`ep-etapa ep-etapa--${situacao}`}>
                <span className="ep-etapa__label">{ETAPA_LABEL[etapa]}</span>
                <Badge variant={SITUACAO_VARIANT[situacao]} size="sm" dot>
                  {SITUACAO_LABEL[situacao]}
                </Badge>
              </div>
            );
          })}
        </div>

        {/* ── Console ── */}
        {requestsQ.isLoading ? (
          <p className="ep-muted">Buscando o registro de emissão…</p>
        ) : console.length === 0 ? (
          <p className="ep-muted">
            Nenhuma requisição de emissão registrada para este documento ainda.
          </p>
        ) : (
          <div className="ep-console" role="log">
            {console.map((linha, i) => (
              <div key={i} className={`ep-linha ep-linha--${linha.marca.toLowerCase()}`}>
                <span className="ep-marca">[{linha.marca}]</span>
                <span>{linha.texto}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Downloads ── */}
        <div className="ep-downloads">
          {request?.danfeUrl ? (
            <a className="ep-link" href={request.danfeUrl} target="_blank" rel="noreferrer">
              Abrir DANFE
            </a>
          ) : null}
          {/* `GET /fiscal/documents/:id/xml` e `/danfe` existem e respondem 404
              com "Document storage integration is pending" — o próprio código
              anota o TODO. Um botão de download aqui erraria sempre, então a
              tela diz o que é. */}
          <span className="ep-hint">
            {request?.hasXml
              ? 'O XML autorizado está guardado; o download pela API depende da integração de armazenamento de documentos, ainda pendente.'
              : 'O XML autorizado aparece aqui quando a SEFAZ o devolve.'}
          </span>
        </div>

        <p className="ep-hint">
          Fora deste painel, por não ter fonte: {SEM_FONTE.join(' e ')} — nada no
          backend registra essas etapas a partir da emissão.
        </p>
      </div>

      <style>{`
        .ep-card { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
        .ep-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; }
        .ep-section { font-family: Inter, sans-serif; font-size: 20px; line-height: 28px; font-weight: 600; color: #191c1e; margin: 0; }
        .ep-hint { font-size: 12px; line-height: 18px; color: #43474e; margin: 2px 0 0; }
        .ep-muted { font-size: 12px; color: #74777f; margin: 0; }
        .ep-etapas { display: flex; flex-direction: column; gap: 4px; }
        .ep-etapa { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 8px 12px; border-radius: 4px; border-left: 3px solid #c4c6cf; background: #f7f9fb; }
        .ep-etapa--concluida { border-left-color: #1a6b33; }
        .ep-etapa--andamento { border-left-color: #002045; background: #f2f4f6; }
        .ep-etapa--erro { border-left-color: #ba1a1a; background: #ffdad6; }
        .ep-etapa__label { font-size: 12px; color: #191c1e; }
        .ep-console { display: flex; flex-direction: column; gap: 2px; padding: 12px; border-radius: 4px; background: #191c1e; }
        .ep-linha { display: flex; gap: 8px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; line-height: 18px; color: #eff1f3; }
        .ep-marca { flex: 0 0 auto; color: #86a0cd; }
        .ep-linha--ok .ep-marca { color: #7ddc93; }
        .ep-linha--erro .ep-marca { color: #ffb4ab; }
        .ep-linha--wait .ep-marca { color: #f2bc82; }
        .ep-downloads { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
        .ep-link { font-size: 12px; color: #002045; font-weight: 600; text-decoration: underline; }
      `}</style>
    </Card>
  );
}
